/**
 * @file useBuilderSelection.ts
 * @description Выбор в конструкторе сайта из адреса и его смена
 * (phase19.16 Р1-6а; uiux-builder-shell.md §4; правила адреса —
 * lib/site-builder/selection.ts).
 *
 * Выбор блока и вкладки пишутся заменой адреса, смена страницы, экрана или
 * раздела — новой записью истории («назад» браузера возвращает). Перед
 * сменой спрашивается `guard` (несохранённый блок): «нет» — выбор не
 * меняется.
 *
 * `extra` — служебные параметры адреса поверх выбора (phase19.16, приёмка
 * Р2а §7.17 п. 3: «Сменить шрифт ›» открывает «Шрифты» с `font`, `back`,
 * `backLabel`); следующий выбор их не переносит.
 */

import { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import {
  isNavigation,
  parseSelection,
  selectionToParams,
  type BuilderSelection,
} from "../../lib/site-builder/selection";

/** Поля выбора, смена которых уводит из панели — открытая группа `?group=` не переносится. */
const LEAVES_GROUP: ReadonlyArray<keyof BuilderSelection> = ["pageId", "screen", "sel", "panel", "sub"];

export interface BuilderSelectionApi {
  selection: BuilderSelection;
  /** Сменить выбор: `select({ panel: "seo" })`. Возвращает, сменился ли. */
  select: (patch: Partial<BuilderSelection>, extra?: Readonly<Record<string, string>>) => Promise<boolean>;
  /** Проверка перед сменой (несохранённые правки); задаёт панель блока. */
  setGuard: (guard: (() => Promise<boolean>) | null) => void;
  /**
   * Спросить ту же проверку без смены адреса: уход, который адрес не меняет
   * (планшет: «←» к структуре закрывает панель, выбор остаётся). true — можно.
   */
  leave: () => Promise<boolean>;
}

export function useBuilderSelection(): BuilderSelectionApi {
  const [params, setParams] = useSearchParams();
  const selection = useMemo(() => parseSelection(params), [params]);
  const guardRef = useRef<(() => Promise<boolean>) | null>(null);
  const latest = useRef(selection);
  latest.current = selection;

  const select = useCallback(
    async (patch: Partial<BuilderSelection>, extra?: Readonly<Record<string, string>>) => {
      const prev = latest.current;
      const next: BuilderSelection = { ...prev, ...patch };
      // Другая страница или экран — выбор на странице и версия истории сбрасываются.
      if (patch.pageId !== undefined || patch.screen !== undefined) {
        if (next.pageId !== prev.pageId || next.screen !== prev.screen) next.sel = patch.sel ?? { kind: "page" };
      }
      if (next.panel !== "history") next.version = null;
      // Группа (SB122) принадлежит панели: другой раздел, вкладка, выбор или страница без явной группы — сброс.
      if (patch.group === undefined && LEAVES_GROUP.some((key) => key in patch)) next.group = null;
      const query = selectionToParams(next);
      for (const [key, value] of Object.entries(extra ?? {})) query.set(key, value);
      const target = query.toString();
      if (target === selectionToParams(prev).toString()) return true;
      if (guardRef.current && !(await guardRef.current())) return false;
      setParams(new URLSearchParams(target), { replace: !isNavigation(prev, next) });
      return true;
    },
    [setParams],
  );

  const setGuard = useCallback((guard: (() => Promise<boolean>) | null) => {
    guardRef.current = guard;
  }, []);

  const leave = useCallback(async () => (guardRef.current ? guardRef.current() : true), []);

  return { selection, select, setGuard, leave };
}
